import Breadcrumb, { Crumb, Separator } from "@asphalt-react/breadcrumb";
import Button from "@asphalt-react/button";
import Dropdown from "@asphalt-react/dropdown";
import Flag from "@asphalt-react/flag";
import Stack from "@asphalt-react/stack";
import TextField from "@asphalt-react/textfield";
import React, { useContext, useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import AssetSelector from "../../common/components/AssetSelector";
import { UserContext } from "../../common/hooks/UserContext";
import routes from "../../common/models/route-constants";
import LoadingPage from "../../common/pages/LoadingPage";
import { arrayOfStringsToArrayOfIdKeyObjects } from "../../common/utils/generalUtils";
import {
  createIcon,
  getCombinedName,
  getIconNameComponentCategories,
} from "../api/iconsApi";
import {
  getIconNameError,
  validIconFileSize,
  validIconName,
} from "../validations/iconValidations";
import styles from "./CreateIconPage.module.css";

function CreateIconPage() {
  const content =
    "1. All lowercase characters\n2. No special characters except '_'\n3. No numbers";

  let history = useHistory();
  const location = useLocation();

  // Context state
  const { user } = useContext(UserContext);

  // page loading state
  const [isLoading, setIsLoading] = useState(false);

  // Input value states
  const [iconCategories, setIconCategories] = useState([]);
  const [iconSizes, setIconSizes] = useState([]);
  const [selectedIconCategory, setSelectedIconCategory] = useState("");
  const [selectedIconSize, setSelectedIconSize] = useState("");
  const [iconName, setIconName] = useState(null);
  const [iconFile, setIconFile] = useState(null);

  // Error states
  const [iconCategoryDropdownError, setIconCategoryDropdownError] =
    useState(null);
  const [iconSizeDropdownError, setIconSizeDropdownError] = useState(null);
  const [iconNameError, setIconNameError] = useState(null);
  const [assetSelectorError, setAssetSelectorError] = useState(null);

  function buildNotification(iconId, name) {
    return {
      title: "Icon added",
      content: `${getCombinedName(name)} has been added successfully`,
      ctaText: "View",
      iconId: iconId,
    };
  }

  // Cancel button click
  const handeCancelClick = () => {
    //TODO : Check for any changes and give user a prompt
    history.goBack();
  };

  // Save Button click
  const handleSaveClick = async () => {
    // Check if Icon Category is selected
    if (selectedIconCategory) {
      setIconCategoryDropdownError(null);
    } else {
      setIconCategoryDropdownError("Icon category cannot be empty");
    }

    // Check if Icon Size is selected
    if (selectedIconSize) {
      setIconSizeDropdownError(null);
    } else {
      setIconSizeDropdownError("Icon size cannot be empty");
    }

    // Check if icon name passes validations
    if (validIconName(iconName)) {
      setIconNameError(null);
    } else {
      setIconNameError(getIconNameError(iconName));
    }

    //Check if icon file is selected
    if (iconFile) {
      setAssetSelectorError(null);
    } else {
      setAssetSelectorError("Icon file cannot be empty");
    }

    if (
      selectedIconCategory &&
      selectedIconSize &&
      validIconName(iconName) &&
      iconFile
    ) {
      setIsLoading(true);

      // Check if icon file dimensions are the same as the icon size selected
      const isIconFileSizeValid = await validIconFileSize(
        iconFile,
        selectedIconSize.key
      );

      if (isIconFileSizeValid) {
        var name = {
          icon_category: selectedIconCategory.key,
          icon_size: selectedIconSize.key,
          icon_name: iconName,
        };

        try {
          const iconId = await createIcon(name, iconFile, user.uid);
          setIsLoading(false);
          history.push(`/${routes.ICONS}/${routes.ASSETS}`, {
            notification: buildNotification(iconId, name),
          });
        } catch (error) {
          setIsLoading(false);
          if (error.name === "FirebaseError") {
            console.log(error.message);
          } else if (
            error.message === "Icon with the same name already exists"
          ) {
            setIconNameError(error.message);
          } else {
            console.log(error);
          }
        }
      } else {
        setIsLoading(false);
        setAssetSelectorError(
          `Dimensions of Icon file must be ${selectedIconSize.key} x ${selectedIconSize.key}`
        );
      }
    }
  };

  useEffect(() => {
    getIconNameComponentCategories()
      .then((querySnapshot) => {
        querySnapshot.forEach(function (doc) {
          if (doc.id === "icon_category") {
            setIconCategories(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
          if (doc.id === "icon_size") {
            setIconSizes(
              arrayOfStringsToArrayOfIdKeyObjects(doc.data().values)
            );
          }
        });
      })
      .catch((error) => {
        console.log(error);
      });
  }, [setIconCategories, setIconSizes, location]);

  return (
    <React.Fragment>
      {isLoading ? (
        <div className={styles.loadingContainer}>
          <LoadingPage />
        </div>
      ) : null}
      <div className={styles.createPageContainer}>
        <div className={styles.breadcrumbContainer}>
          <Breadcrumb>
            <Crumb
              text="assets"
              tagProps={{
                to: `/${routes.ICONS}/${routes.ASSETS}`,
              }}
              as={Link}
            />
            <Separator />
            <Crumb
              text="add icon"
              tagProps={{
                to: `/${routes.ICONS}/${routes.ASSETS}/${routes.CREATE}`,
              }}
              as={Link}
            />
          </Breadcrumb>
        </div>
        <div>
          <h3 className={styles.heading}>Add Icon</h3>
          <div className={styles.flagContainer}>
            <Flag page info title="How to name your icon" content={content} />
          </div>
          <div className={styles.formRow}>
            <div className={styles.iconCategoryContainer}>
              <p className={styles.inputLabel}>Icon Category</p>
              <div className={styles.inputContainer}>
                <Dropdown
                  typeahead
                  items={iconCategories}
                  onChange={(selectedProductCategory) => {
                    setSelectedIconCategory(selectedProductCategory);
                    setIconCategoryDropdownError(null);
                  }}
                  stretch
                  invalid={!!iconCategoryDropdownError}
                />
              </div>
              {iconCategoryDropdownError ? (
                <Flag error title={iconCategoryDropdownError} />
              ) : null}
            </div>
            <div className={styles.iconSizeContainer}>
              <p className={styles.inputLabel}>Icon Size</p>
              <div className={styles.inputContainer}>
                <Dropdown
                  items={iconSizes}
                  onChange={(selectedIconSize) => {
                    setSelectedIconSize(selectedIconSize);
                    setIconSizeDropdownError(null);
                  }}
                  stretch
                  invalid={!!iconSizeDropdownError}
                />
              </div>
              {iconSizeDropdownError ? (
                <Flag error title={iconSizeDropdownError} />
              ) : null}
            </div>
            <div className={styles.iconNameContainer}>
              <p className={styles.inputLabel}>Icon Name</p>
              <div className={styles.inputContainer}>
                <TextField
                  required={!!iconNameError}
                  size="m"
                  stretch
                  placeholder="Icon Name"
                  type="text"
                  invalid={!!iconNameError}
                  onChange={(e) => {
                    setIconName(e.target.value);
                    setIconNameError(null);
                  }}
                />
              </div>
              {iconNameError ? <Flag error title={iconNameError} /> : null}
            </div>
          </div>

          <div className={styles.assetSelectorContainer}>
            <div className={styles.inputContainer}>
              <AssetSelector
                heading="Icon File"
                onAssetSelected={(file) => {
                  if (file) {
                    setIconFile(file);
                    setAssetSelectorError(null);
                  }
                }}
              />
            </div>
            {assetSelectorError ? (
              <Flag error title={assetSelectorError} />
            ) : null}
          </div>
          <Stack>
            <Button primary onClick={handleSaveClick}>
              Save
            </Button>
            <Button secondary onClick={handeCancelClick}>
              Cancel
            </Button>
          </Stack>
        </div>
      </div>
    </React.Fragment>
  );
}

export default CreateIconPage;
